// Background RTT prober for the configured regions. Probes each region's
// HTTP endpoint (derived from the WS URL) and exposes the latest map of
// region id → RTT ms via subscribe(). Caches values in localStorage as
// hints so the very first dropdown render isn't blank on cold boot.
//
// The probe is a HEAD request with mode: 'no-cors' so we don't need CORS
// configured on the Colyseus servers — the response is opaque, but the
// round-trip time is what we want. First request per region warms the
// connection (DNS + TCP + TLS), subsequent requests reuse keep-alive so
// the value converges to the actual RTT after a couple of cycles.

import { REGIONS, type Region } from './regions';
import { loadRegionPingHints, saveRegionPingHints, type RegionPingHints } from '../ui/regionSettings';

export type { RegionPingHints };

const PROBE_INTERVAL_MS = 5_000;
const PROBE_TIMEOUT_MS = 4_000;

type Listener = (map: Readonly<RegionPingHints>) => void;

const latest: RegionPingHints = { ...loadRegionPingHints() };
const listeners = new Set<Listener>();
let timer: ReturnType<typeof setInterval> | null = null;
let probing = false;

function notify(): void {
  const snapshot = { ...latest };
  for (const cb of Array.from(listeners)) {
    try { cb(snapshot); } catch (err) { console.warn('[regionPing] listener threw', err); }
  }
}

function httpFromWs(wsUrl: string): string {
  return wsUrl.replace(/^wss:/i, 'https:').replace(/^ws:/i, 'http:');
}

async function probeOne(region: Region): Promise<number | null> {
  const baseUrl = httpFromWs(region.url).replace(/\/$/, '');
  // Cache-busting query keeps the browser from short-circuiting the
  // request when it sees the same URL between probes.
  const url = `${baseUrl}/ping?_p=${Date.now()}`;
  const ctrl = new AbortController();
  const timeoutId = setTimeout(() => ctrl.abort(), PROBE_TIMEOUT_MS);
  const start = performance.now();
  try {
    await fetch(url, { method: 'HEAD', mode: 'no-cors', signal: ctrl.signal, cache: 'no-store' });
    return Math.max(1, Math.round(performance.now() - start));
  } catch {
    return null;
  } finally {
    clearTimeout(timeoutId);
  }
}

async function probeAll(): Promise<void> {
  if (probing) return;
  probing = true;
  try {
    const results = await Promise.allSettled(REGIONS.map((r) => probeOne(r)));
    let changed = false;
    for (let i = 0; i < REGIONS.length; i++) {
      const r = REGIONS[i]!;
      const result = results[i];
      if (result?.status === 'fulfilled' && result.value != null) {
        if (latest[r.id] !== result.value) {
          latest[r.id] = result.value;
          changed = true;
        }
      }
    }
    if (changed) {
      saveRegionPingHints(latest);
      notify();
    }
  } finally {
    probing = false;
  }
}

export function startProbing(): void {
  if (timer) return;
  void probeAll();
  timer = setInterval(() => { void probeAll(); }, PROBE_INTERVAL_MS);
}

export function stopProbing(): void {
  if (timer) { clearInterval(timer); timer = null; }
}

export function getCurrentPings(): Readonly<RegionPingHints> {
  return { ...latest };
}

/** Subscribe to live ping updates. Fires immediately with the current
 *  snapshot (so consumers don't need a separate initial-load step). */
export function subscribe(cb: Listener): () => void {
  listeners.add(cb);
  cb({ ...latest });
  return () => { listeners.delete(cb); };
}
